import { authApi } from '@/api/auth';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { AltchaWidget } from '@/features/captcha/altcha-widget';
import { useAltchaCaptcha } from '@/features/captcha/use-altcha-captcha';
import { resolveReauth } from '@/lib/reauth-gate';
import { useAuthStore } from '@/stores/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { SsoButtons } from './SsoButtons';

const schema = z.object({
  email: z.string().min(1),
  password: z.string().min(1),
});

type Values = z.infer<typeof schema>;

export default function ReauthModal() {
  const { t } = useTranslation();
  const open = useAuthStore((s) => s.reauthRequired);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cap = useAltchaCaptcha();
  const [capToken, setCapToken] = useState<string | null>(null);
  const [captchaNonce, setCaptchaNonce] = useState(0);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });
  const errors = form.formState.errors;

  async function onSubmit(v: Values) {
    setError(null);
    setLoading(true);
    try {
      if (cap.enabled && !capToken) {
        setError(t('auth.captcha_required', 'Please complete the verification'));
        return;
      }
      const { access } = await authApi.login(v.email, v.password, capToken ?? undefined);
      useAuthStore.getState().setAccess(access);
      resolveReauth();
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data
        ?.error?.code;
      setCapToken(null);
      setCaptchaNonce((n) => n + 1);
      setError(
        code === 'ERR_AUTH_CAPTCHA_FAILED'
          ? t('auth.captcha_failed', 'Captcha verification failed — please try again.')
          : t('auth.reauth_failed', 'Login failed. Please try again.'),
      );
    } finally {
      setLoading(false);
    }
  }

  function onGiveUp() {
    useAuthStore.getState().clear();
    window.location.assign('/login');
  }

  return (
    <Dialog open={open} onOpenChange={() => undefined}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-[420px]"
        // Losing the session is not something the user can dismiss away: the
        // only ways forward are signing in or signing out.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-1">
          <DialogTitle className="text-md font-semibold">
            {t('auth.reauth_title', 'Session expired — sign in to continue')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t('auth.reauth_title', 'Session expired — sign in to continue')}
          </DialogDescription>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/*
          Without this an SSO-only account has no way out of the modal: the
          password form always rejects it, and the only alternative is giving up
          and reloading /login. The provider click is a full-page navigation, so
          the callback's refresh cookie + bootstrap() restores the session
          without needing resolveReauth().
        */}
        <SsoButtons returnTo={window.location.pathname} />

        {/* `mt-5` because SsoButtons' divider only carries its own top gap. */}
        <form className="mt-5" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <FieldGroup className="gap-4">
            <Field data-invalid={!!errors.email}>
              <FieldLabel htmlFor="reauth-email">{t('auth.email', 'Email')}</FieldLabel>
              <Input
                id="reauth-email"
                type="email"
                autoComplete="username"
                aria-label="email"
                aria-invalid={!!errors.email}
                {...form.register('email')}
              />
            </Field>
            <Field data-invalid={!!errors.password}>
              <FieldLabel htmlFor="reauth-password">{t('auth.password', 'Password')}</FieldLabel>
              <Input
                id="reauth-password"
                type="password"
                autoComplete="current-password"
                aria-label="password"
                aria-invalid={!!errors.password}
                {...form.register('password')}
              />
              <FieldError>{errors.password?.message}</FieldError>
            </Field>
          </FieldGroup>

          {cap.enabled && cap.challengeUrl && (
            <div className="mt-3">
              <AltchaWidget
                key={captchaNonce}
                challengeUrl={cap.challengeUrl}
                onToken={setCapToken}
                onReset={() => setCapToken(null)}
                onError={() => setCapToken(null)}
              />
            </div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onGiveUp}>
              {t('auth.logout', 'Log out')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Spinner data-icon="inline-start" />}
              {t('auth.reauth_submit', 'Sign in')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
